import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';

/**
 * UC-S09 STU-11-FR-08 + MC-09 v1-1 — the learner's safety report, the Trust &
 * Safety inbox that reads it, and the governed operation that decides whether
 * the intake may be live. `getSafetyReportAvailability` is how the client
 * learns whether to draw the form at all: the server answers yes only when the
 * `safety_reports` flag is on AND the nine MC-09 activation conditions are met
 * AND the learner is inside the declared age range.
 */

export type SafetyReportCategory =
  | 'FELT_UNSAFE'
  | 'INAPPROPRIATE_CONTENT'
  | 'PRIVATE_CONTACT_REQUEST'
  | 'BULLYING'
  | 'OTHER';

export interface SafetyReportInput {
  studentId: string;
  enrollmentId?: string;
  seq?: number;
  /** Optional: MC-09 §5 — the child may pick a reason. */
  category?: SafetyReportCategory;
  /** Optional: MC09-R02 — no explanation is required. */
  description?: string;
  /** Same key on a retry, so a lost answer never files the report twice. */
  requestKey: string;
}

export interface SafetyReportReceipt {
  status: 'RECEIVED';
  receiptId: string;
  receivedAt: string;
}

export interface SafetyAvailability {
  enabled: boolean;
  drill?: boolean;
  /** MC09-AC05 — the attended alternative, shown if sending fails. */
  alternativeChannel?: string;
  /** MC-09 §5 / AC08 — verified immediate-danger guidance, when one country is enabled. */
  urgentGuidance?: string;
}

export type SafetyReportStatus = 'RECEIVED' | 'TRIAGED' | 'ESCALATED';
export type SafetyUrgency = 'URGENT' | 'STANDARD';
export type SafetyEscalationReason = 'URGENT' | 'PRIMARY_UNAVAILABLE' | 'ACK_TIMEOUT';

export interface SafetyInboxCase {
  caseId: string;
  reportId: string;
  category: SafetyReportCategory;
  status: SafetyReportStatus;
  receivedAt: string;
  hoursOpen: number;
  studentId: string;
  enrollmentId?: string;
  seq?: number;
  teacherId?: string;
  assignedTo?: string;
  urgency?: SafetyUrgency;
  escalatedTo?: string;
  escalationReason?: SafetyEscalationReason;
  drill?: boolean;
}

export interface SafetyIntakeState {
  flagOn: boolean;
  ready: boolean;
  drillReady: boolean;
}

export interface SafetyReportAccessEntry {
  action: 'RECEIVED' | 'OPENED' | 'ALERTED' | 'ALERT_FAILED' | 'TRIAGED' | 'ESCALATED';
  actorId: string;
  at: string;
  detail?: string;
}

export interface SafetyReportDetail {
  report: SafetyInboxCase & {
    description: string;
    reporterId: string;
    courseId?: string;
    triagedAt?: string;
  };
  access: SafetyReportAccessEntry[];
}

// ── MC-09 governed operation ────────────────────────────────────────────

export type SafetyActivationCondition =
  | 'OWNERS'
  | 'COVERAGE'
  | 'PREVENTIVE_MEASURE'
  | 'PRIORITIES_AND_TIMES'
  | 'COUNTRY_PROCEDURE'
  | 'PERMISSIONS'
  | 'CONTINUITY'
  | 'AGE_RANGE'
  | 'OPERATIONAL_DRILL';

export type DrillApprover = 'PRIMARY' | 'BACKUP' | 'OPERATIONS';

export interface SafetyOnCallOwner {
  operatorId: string;
  role: string;
  alertEmail: string;
  otherContact?: string;
}

export interface SafetyOperationsInput {
  primary?: SafetyOnCallOwner;
  backup?: SafetyOnCallOwner;
  coverage?: {
    timezone: string;
    windows: Array<{ days: string[]; start: string; end: string }>;
    outOfHours: string;
    absenceSubstitution: string;
  };
  preventiveMeasure?: {
    mechanism: 'PROVISIONAL_MANUAL';
    description: string;
    authority: string;
    scope: string;
    coversLearnerAccessCut: boolean;
    documentRef: string;
    reviewDate: string;
  };
  procedure?: {
    documentRef: string;
    approvedBy: string;
    approvedAt: string;
    acknowledgeWithinMinutes: number;
  };
  countries?: Array<{
    countryCode: string;
    urgentProcedureRef: string;
    learnerGuidance: string;
    verifiedBy: string;
    verifiedAt: string;
    legalReviewRef: string;
  }>;
  permissions?: { documentRef: string; reviewedBy: string; reviewedAt: string };
  continuity?: { alternativeChannel: string; deliveryFailureProcedureRef: string };
  ageRange?: { min: number; max: number };
  drill?: {
    completedAt: string;
    evidenceRef: string;
    escalatedToBackup: boolean;
    preventiveMeasureExecuted: boolean;
  };
}

export interface SafetyOperationsConfig extends SafetyOperationsInput {
  version: number;
  updatedBy: string;
  updatedAt: string;
  reason: string;
}

export interface SafetyOperationsView {
  config?: SafetyOperationsConfig;
  assessment: {
    ready: boolean;
    drillReady: boolean;
    configVersion?: number;
    conditions: Array<{ condition: SafetyActivationCondition; met: boolean; missing: string[] }>;
  };
  acceptances: Array<{ position: 'PRIMARY' | 'BACKUP'; operatorId: string; acceptedAt: string }>;
  drillApprovals: Array<{
    approver: DrillApprover;
    operatorId: string;
    configVersion: number;
    approvedAt: string;
  }>;
  history: Array<{ version: number; updatedBy: string; updatedAt: string; reason: string }>;
}

export const getSafetyReportAvailability = (studentId: string) =>
  apiGet<SafetyAvailability>(
    `/api/v1/learning/safety-reports/availability?studentId=${encodeURIComponent(studentId)}`,
  );

export const submitSafetyReport = (input: SafetyReportInput) =>
  apiPost<SafetyReportReceipt>('/api/v1/learning/safety-reports', input);

export const getSafetyInbox = () =>
  apiGet<{ intake: SafetyIntakeState; cases: SafetyInboxCase[] }>('/api/v1/admin/safety/reports');

export const openSafetyReport = (reportId: string) =>
  apiGet<SafetyReportDetail>(`/api/v1/admin/safety/reports/${encodeURIComponent(reportId)}`);

export const triageSafetyReport = (reportId: string, urgency: SafetyUrgency) =>
  apiPost<SafetyInboxCase>(`/api/v1/admin/safety/reports/${encodeURIComponent(reportId)}/triage`, {
    urgency,
  });

export const escalateSafetyReport = (
  reportId: string,
  reason: Exclude<SafetyEscalationReason, 'ACK_TIMEOUT'>,
) =>
  apiPost<SafetyInboxCase>(
    `/api/v1/admin/safety/reports/${encodeURIComponent(reportId)}/escalate`,
    { reason },
  );

export const getSafetyOperations = () =>
  apiGet<SafetyOperationsView>('/api/v1/admin/safety/operations');

export const updateSafetyOperations = (
  input: SafetyOperationsInput & { reason: string; expectedVersion?: number },
) => apiPut<SafetyOperationsView>('/api/v1/admin/safety/operations', input);

export const acceptSafetyAssignment = () =>
  apiPost<SafetyOperationsView>('/api/v1/admin/safety/operations/acceptance', {});

export const approveSafetyDrill = (approver: DrillApprover) =>
  apiPost<SafetyOperationsView>('/api/v1/admin/safety/operations/drill-approvals', { approver });
